// Confirm-time validation for the System Identification form. Returns one
// human-readable string per problem, so the error box can list them all at once.
import { highestAssignedSSR, highestAssignedUOS } from './highestAssigned';

// A tgs switch renders FakeSelectBox in place of both the SSR quantity and the
// SSR rating controls (InitialUOSInfo.js), so it carries no SSR at all. A site
// built entirely of gas switches genuinely has none and must stay confirmable.
// The comparison is exact: a combined 'tgs/tes' switch still gets real SSR
// controls, so it still needs an SSR to point at.
const needsAnSSR = (switches) =>
  (switches ?? []).some((eachSwitch) => eachSwitch?.heatingSys !== 'tgs');

export const collectConfirmProblems = ({
  locationName,
  civicAddress,
  numOfUOS,
  numOfSSR,
  numSwitches,
  switches,
}) => {
  const problems = [];

  if (!/^.*-.*-.*$/.test(locationName ?? '')) {
    problems.push('location name needs 2 dashes — e.g. ACME-MAIN STREET-MST');
  }

  if (!civicAddress) {
    problems.push('civic address is empty');
  }

  // Confirming with these blank writes uos_panel_number: 0 and ssr_quantity: 0,
  // which the form reads back as null — leaving both dropdowns empty next time
  // the location is opened, with no way to refill them but retyping the number.
  // Number('') and Number(null) are both 0, so one check covers blank and zero.
  const uosCount = Number(numOfUOS);
  const ssrCount = Number(numOfSSR);
  const switchCount = Number(numSwitches);

  if (uosCount === 0) {
    problems.push('number of uos panels is 0');
  } else if (!Number.isInteger(uosCount) || uosCount < 0) {
    problems.push('number of uos panels must be a positive whole number');
  }

  if (!Number.isInteger(ssrCount) || ssrCount < 0) {
    problems.push('ssr qty. must be a non-negative whole number');
  } else if (ssrCount === 0 && needsAnSSR(switches)) {
    problems.push('ssr qty. is 0');
  }

  // A separate rule from "is it filled in". Set the panel count to 3, put a
  // switch on uos 03, then lower the count to 1: the switch still references a
  // panel the dropdown no longer offers and the site no longer claims to have.
  // Only checked once the count is non-zero, since zero has its own message and
  // saying it twice helps nobody.
  const uosAssigned = highestAssignedUOS(switches);
  if (Number.isInteger(uosCount) && uosCount > 0 && uosAssigned > uosCount) {
    problems.push(
      `uos ${String(uosAssigned).padStart(
        2,
        '0',
      )} is assigned but there are only ${uosCount} uos panels`,
    );
  }

  const ssrAssigned = highestAssignedSSR(switches);
  if (Number.isInteger(ssrCount) && ssrCount > 0 && ssrAssigned > ssrCount) {
    problems.push(
      `ssr${ssrAssigned} is assigned but there are only ${ssrCount} ssrs`,
    );
  }

  if (switchCount === 0) {
    problems.push('number of switches is 0');
  } else if (!Number.isInteger(switchCount) || switchCount < 0) {
    problems.push('number of switches must be a positive whole number');
  }

  const unassigned = (switches ?? [])
    .map((eachSwitch, idx) => (eachSwitch.UOS ? null : idx + 1))
    .filter(Boolean);

  if (unassigned.length > 0) {
    const label = unassigned.length === 1 ? 'switch' : 'switches';
    problems.push(`${label} ${unassigned.join(', ')} — no UOS assigned`);
  }

  return problems;
};
